import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import * as d3 from "d3";
// eslint-disable-next-line import/order
import isFunction from "lodash/isFunction";
import { backgroundDarkStyle, backgroundLightStyle } from "./networkConfig";
import "./SocietyNetwork.css";

import {
  handleMaxNodesMaxLinks,
  changeLinksDisplay,
  changeNodesDisplay,
  changeBackgroundColor,
  changeSubGroupDisplay,
  bindingsToNodesLinks,
  addCenteredKey,
} from "./drawNetwork/SNAutils";

// eslint-disable-next-line import/no-named-as-default-member
import drawNetwork from "./drawNetwork";
import { countOptions } from "../../../../../../config/config.sna";
import GraphAnalysis from "../../../../../../common/utils/snaAnalysis/snaAnalysis";

import { getRows } from "../../../../service/datasetService";

// 限制可 RENDER 的 NODES 數量,避免超過瀏覽器負荷
const RENDER_NODES_LIMIT = 200;

const SocietyNetwork = (props) => {
  const {
    data,
    group,
    maxLinks,
    maxNodes,
    limitTotalNodesCount,
    backgroundStyle,
    addNodeCount,
    legendShowNodDataGroup,
    showSubGroup,
    nodeCountMeetAuthNew,
    placeNodeConfig,
    placeNodeMode,
    statisticDispersion,
    onGroupNetworkDataReady,
    myRef, // 由 parent 建立, 傳給 component 使用, 才可以順利匯出圖片
    colors,
    snaWidth,
    snaHeight,
    snaDivId,
    onClickNode,
    onMouseOverNode,
    onMouseOutNode,
    onClickLink,
    alertCallback,
    datasetTotal,
  } = props;

  const [groupNetwork, setGroupNetwork] = useState(null);
  const [links, setLinks] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [curSubGroups, setCurSubGroups] = useState([]);

  /**
   * handle groupNetwork when data from parent or nodeCountMeetAuthNew change
   * node.count will be changed when nodeCountMeetAuthNew change
   * */
  useEffect(() => {
    if (!data) {
      return;
    }
    // 在 simulation 之前，nodes 增加一個屬性 centered: true/false
    // const networkData = bindingsToNodesLinks(data, nodeCountMeetAuth);
    const tmpNodeCountMeetAuth = nodeCountMeetAuthNew === countOptions[0].key;
    const networkData = bindingsToNodesLinks(data, tmpNodeCountMeetAuth);

    // global networkData的count不用動
    if (onGroupNetworkDataReady) onGroupNetworkDataReady(networkData); // pass to parent

    // 當nodeCountMeetAuthNew value是countOptions的item keyName時，使用舊流程
    if (countOptions.map(({ key }) => key).includes(nodeCountMeetAuthNew)) {
      // nodes sort：ORDER BY node.count DESC
      networkData.nodes.sort((a, b) => b.count - a.count);
      // nodes 增加一個屬性 centered: true/false
      networkData.nodes = Object.assign(addCenteredKey(networkData.nodes));
    } else {
      const tmpGraph = new GraphAnalysis(networkData.links, networkData.nodes);
      const tmpRows = getRows(tmpGraph, nodeCountMeetAuthNew);
      // 將分析後的數值，填到networkData的nodes對應id的"count"欄位
      tmpRows.forEach(({ id, value }) => {
        const findObj = networkData.nodes.find((obj) => obj.id === id);
        if (findObj) {
          findObj.count = value;
        }
      });
    }

    setGroupNetwork(networkData);
    setCurSubGroups(networkData.curSubGroups);
  }, [data, nodeCountMeetAuthNew]);

  useEffect(() => {
    if (!groupNetwork || !myRef) {
      return;
    }

    const drawInit = () => {
      // todo: 當 width, height 變動時,只需要調整 svg(width,height) 及 groupLegend(位置)
      const { svg, view, link, node, nodeLabel, groupLegend, subGroupLegend } =
        drawNetwork({
          myRef,
          data: groupNetwork,
          groups: group,
          backgroundStyle,
          addNodeCount,
          legendShowNodDataGroup,
          showSubGroup,
          placeNodeConfig,
          placeNodeMode,
          statisticDispersion,
          snaColors: colors,
          snaWidth,
          snaHeight,
          onClickNode,
          onMouseOverNode,
          onMouseOutNode,
          onClickLink,
          datasetTotal,
        });
    };

    const safeLimitCount = Number.parseInt(limitTotalNodesCount, 10);
    const curNodesCount = groupNetwork.nodes.length;
    if (
      safeLimitCount < 0 ||
      (safeLimitCount > 0 && curNodesCount <= safeLimitCount)
    ) {
      drawInit();
    } else if (isFunction(alertCallback))
      alertCallback(
        `節點數量 ${curNodesCount}, 超過節點顯示限制 ${safeLimitCount},請「縮減時間範圍」或「調整節點數限制」！`
      );
  }, [
    groupNetwork,
    placeNodeConfig,
    placeNodeMode,
    legendShowNodDataGroup,
    statisticDispersion,
    colors,
    snaWidth,
    snaHeight,
    limitTotalNodesCount,
  ]);

  // modify links and nodes when maxLinks or maxNodes change
  useEffect(() => {
    if (!(groupNetwork && groupNetwork.links && groupNetwork.nodes)) return;

    // 當nodeCountMeetAuthNew value是countOptions的item keyName時，使用舊的流程
    if (countOptions.map(({ key }) => key).includes(nodeCountMeetAuthNew)) {
      const tmpNodeCountMeetAuth = nodeCountMeetAuthNew === countOptions[0].key;
      const [linksModify, nodesModify] = handleMaxNodesMaxLinks({
        maxLinks,
        maxNodes,
        links: groupNetwork.links,
        nodes: groupNetwork.nodes,
        tmpNodeCountMeetAuth,
      });
      setLinks(linksModify);
      setNodes(nodesModify);
    }
  }, [maxLinks, maxNodes, nodeCountMeetAuthNew, groupNetwork]);

  // render links when links change
  useEffect(() => {
    // call func. to render
    changeLinksDisplay(links);
  }, [links]);

  // render nodes when nodes change
  useEffect(() => {
    // call func. to render
    changeNodesDisplay(nodes);
  }, [nodes]);

  // handle backgroundStyle change
  useEffect(() => {
    changeBackgroundColor(backgroundStyle, groupNetwork);
  }, [backgroundStyle]);

  // handle addNodeCount change
  useEffect(() => {
    d3.selectAll(".node-label-node-count").style(
      "display",
      addNodeCount ? "inline" : "none"
    );
  }, [addNodeCount]);

  // handle showSubGroup change
  useEffect(() => {
    changeSubGroupDisplay(curSubGroups, showSubGroup);
  }, [curSubGroups, showSubGroup]);

  const getStyle = () =>
    backgroundStyle === "dark" ? backgroundDarkStyle : backgroundLightStyle;

  return (
    <>
      <div
        ref={myRef}
        id={snaDivId}
        className={snaDivId}
        style={{ ...getStyle() }}
      />
    </>
  );
};

SocietyNetwork.defaultProps = {
  maxLinks: null,
  maxNodes: null,
  limitTotalNodesCount: RENDER_NODES_LIMIT,
  addNodeCount: false,
  backgroundStyle: "light",
  showSubGroup: false,
  nodeCountMeetAuthNew: "",
  legendShowNodDataGroup: false,
  placeNodeMode: "centerAndEdge",
  statisticDispersion: 10,
  onGroupNetworkDataReady: null,
  myRef: null,
  colors: null,
  snaWidth: 0,
  snaHeight: 0,
  snaDivId: "sna_display_area",
  onClickNode: () => null,
  onMouseOverNode: () => null,
  onMouseOutNode: () => null,
  onClickLink: () => null,
  alertCallback: () => null,
  datasetTotal: 0,
};

SocietyNetwork.propTypes = {
  data: PropTypes.arrayOf(PropTypes.any).isRequired,
  group: PropTypes.arrayOf(PropTypes.string).isRequired,
  maxLinks: PropTypes.number,
  maxNodes: PropTypes.number,
  limitTotalNodesCount: PropTypes.number,
  addNodeCount: PropTypes.bool,
  backgroundStyle: PropTypes.string,
  showSubGroup: PropTypes.bool,
  nodeCountMeetAuthNew: PropTypes.string,
  legendShowNodDataGroup: PropTypes.bool,
  placeNodeConfig: PropTypes.objectOf(PropTypes.any).isRequired,
  placeNodeMode: PropTypes.string,
  statisticDispersion: PropTypes.number,
  onGroupNetworkDataReady: PropTypes.func,
  myRef: PropTypes.objectOf(PropTypes.any),
  colors: PropTypes.arrayOf(PropTypes.string),
  snaWidth: PropTypes.number,
  snaHeight: PropTypes.number,
  snaDivId: PropTypes.string,
  onClickNode: PropTypes.func,
  onMouseOverNode: PropTypes.func,
  onMouseOutNode: PropTypes.func,
  onClickLink: PropTypes.func,
  alertCallback: PropTypes.func,
  datasetTotal: PropTypes.number,
};

export default SocietyNetwork;
